/**
 * Quiet Hours music bed — pre-generated instrumental backing tracks.
 *
 * One MP3 per (category, duration), generated at build time by the
 * voice-pipeline tool from Google Lyria 3 Pro (`fal-ai/lyria3/pro`),
 * then loop-padded with ffmpeg to match the session length plus a
 * gentle tail. Lives at
 *   `public/generated/quiet-hours/music/<category>/<duration>.mp3`.
 *
 * The bed plays at a lower gain than the voice (~0.55) so the voice
 * sits above without being drowned. When voice is absent (unguided
 * mode, or missing voice file), the bed plays alone.
 *
 * Mute respect: like `sound.ts`, we honor `audio.isMuted()` at
 * `startMusic()` time. A mid-session mute toggle will not silence an
 * already-playing track — re-enter the session to apply.
 */
import { audio } from '@/juice';

import type { QuietHoursCategorySlug, DurationMinutes } from './categories';

/** Public URL of the pre-generated music bed MP3. */
export function musicFileUrl(
  category: QuietHoursCategorySlug,
  duration: DurationMinutes,
): string {
  return `/generated/quiet-hours/music/${category}/${duration}.mp3`;
}

const probeCache = new Map<string, boolean>();
export async function musicFileExists(
  category: QuietHoursCategorySlug,
  duration: DurationMinutes,
): Promise<boolean> {
  const url = musicFileUrl(category, duration);
  if (probeCache.has(url)) return probeCache.get(url)!;
  try {
    const res = await fetch(url, { method: 'HEAD' });
    const ok = res.ok;
    probeCache.set(url, ok);
    return ok;
  } catch {
    probeCache.set(url, false);
    return false;
  }
}

export interface MusicPlayback {
  stop(): void;
  pause(): void;
  resume(): void;
  /** Underlying audio element (for diagnostics / future ducking). */
  readonly el: HTMLAudioElement;
}

/**
 * Begin music-bed playback. Returns null when muted, missing, or
 * outside a browser. The fade-in is achieved by ramping the audio
 * element's `volume` over ~2.5 s with a small JS timer — keeping it
 * out of Web Audio means the same plain `<audio>` element can be
 * paused/resumed alongside the voice without re-routing graph nodes.
 */
export function playMusic(
  category: QuietHoursCategorySlug,
  duration: DurationMinutes,
  opts: { volume?: number; fadeInMs?: number } = {},
): MusicPlayback | null {
  if (audio.isMuted()) return null;
  if (typeof window === 'undefined') return null;

  const url = musicFileUrl(category, duration);
  const el = new Audio(url);
  el.preload = 'auto';
  el.loop = false; // pre-rendered at exact session length; no looping needed
  el.crossOrigin = 'anonymous';

  const target = opts.volume ?? 0.55;
  const fadeInMs = opts.fadeInMs ?? 2500;
  el.volume = 0.0001;

  // Fade-in via a short rAF loop. Cheap, no Web Audio dependency.
  const t0 = performance.now();
  let fadeRaf = 0;
  let fadeStopped = false;
  const fadeTick = () => {
    if (fadeStopped) return;
    const t = (performance.now() - t0) / fadeInMs;
    if (t >= 1) {
      el.volume = target;
      return;
    }
    // ease-out for a gentle arrival
    const eased = 1 - Math.pow(1 - t, 2);
    el.volume = Math.max(0.0001, eased * target);
    fadeRaf = requestAnimationFrame(fadeTick);
  };
  fadeRaf = requestAnimationFrame(fadeTick);

  void el.play().catch(() => {
    /* autoplay may fail on cold load; silent fail keeps the room alive */
  });

  return {
    el,
    stop(): void {
      fadeStopped = true;
      if (fadeRaf) cancelAnimationFrame(fadeRaf);
      // Quick fade out so it doesn't snap silent.
      const v0 = el.volume;
      const t0Out = performance.now();
      const fadeOutMs = 1200;
      const out = () => {
        const t = (performance.now() - t0Out) / fadeOutMs;
        if (t >= 1) {
          try {
            el.pause();
            el.src = '';
          } catch {
            /* ignore */
          }
          return;
        }
        el.volume = Math.max(0.0001, v0 * (1 - t));
        requestAnimationFrame(out);
      };
      out();
    },
    pause(): void {
      try {
        el.pause();
      } catch {
        /* ignore */
      }
    },
    resume(): void {
      try {
        void el.play();
      } catch {
        /* ignore */
      }
    },
  };
}
